/**
 * プロンプト履歴管理ユーティリティ
 * LocalStorageを使用してプロンプト履歴を保存・取得する
 *
 * 保存フォーマットは `PromptHistoryEntry[]` (プロンプト本文 + 保存日時)。
 * 旧フォーマット (string[]) で保存された履歴も読み込めるが、
 * 保存日時は記録されていないため savedAt は null になる。
 */

const MAX_HISTORY_ITEMS = 20;

export interface PromptHistoryEntry {
  /** プロンプト本文 */
  prompt: string;
  /** 保存日時 (epoch ms)。旧フォーマットから読み込んだ項目は null */
  savedAt: number | null;
  /**
   * 使用したシナリオの id (動画エディタ AI 編集の編集指示のみ。履歴モーダルの
   * 「このシナリオ」絞り込みに使う)。記録していない項目は undefined
   */
  scenarioId?: string;
}

/** LocalStorage から読み出した任意の値を PromptHistoryEntry[] に正規化する */
function normalizeEntries(raw: unknown): PromptHistoryEntry[] {
  if (!Array.isArray(raw)) return [];
  const entries: PromptHistoryEntry[] = [];
  for (const item of raw) {
    // 旧フォーマット: プロンプト文字列がそのまま入っている
    if (typeof item === 'string') {
      const trimmed = item.trim();
      if (trimmed) entries.push({ prompt: trimmed, savedAt: null });
      continue;
    }
    if (item && typeof item === 'object') {
      const { prompt, savedAt, scenarioId } = item as { prompt?: unknown; savedAt?: unknown; scenarioId?: unknown };
      if (typeof prompt !== 'string') continue;
      const trimmed = prompt.trim();
      if (!trimmed) continue;
      entries.push({
        prompt: trimmed,
        savedAt: typeof savedAt === 'number' && Number.isFinite(savedAt) ? savedAt : null,
        ...(typeof scenarioId === 'string' && scenarioId ? { scenarioId } : {}),
      });
    }
  }
  return entries.slice(0, MAX_HISTORY_ITEMS);
}

/**
 * プロンプト履歴をLocalStorageから取得
 * @param storageKey - LocalStorageのキー
 */
export function getPromptHistory(storageKey: string): PromptHistoryEntry[] {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? normalizeEntries(JSON.parse(stored)) : [];
  } catch {
    return [];
  }
}

/**
 * プロンプト履歴をLocalStorageに保存
 * @param storageKey - LocalStorageのキー
 * @param history - 保存する履歴配列
 */
export function savePromptHistory(storageKey: string, history: PromptHistoryEntry[]): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(history.slice(0, MAX_HISTORY_ITEMS)));
  } catch {
    // ignore
  }
}

/**
 * プロンプトを履歴に追加
 * @param storageKey - LocalStorageのキー
 * @param prompt - 追加するプロンプト
 * @returns 更新後の履歴配列
 */
export function addToPromptHistory(
  storageKey: string,
  prompt: string,
  opts?: { scenarioId?: string | null },
): PromptHistoryEntry[] {
  const history = getPromptHistory(storageKey);
  const trimmed = prompt.trim();
  if (!trimmed) return history;

  // 重複を削除して先頭に追加 (保存日時は最後に使った時刻で更新。シナリオ id も最後に使ったものに更新)
  const filtered = history.filter((entry) => entry.prompt !== trimmed);
  const entry: PromptHistoryEntry = {
    prompt: trimmed,
    savedAt: Date.now(),
    ...(opts?.scenarioId ? { scenarioId: opts.scenarioId } : {}),
  };
  const updated = [entry, ...filtered].slice(0, MAX_HISTORY_ITEMS);
  savePromptHistory(storageKey, updated);
  return updated;
}

// 各機能用のストレージキー
export const PROMPT_HISTORY_KEYS = {
  /** AI俳優生成 */
  ACTOR_GENERATION: 'actor_generation_prompt_history',
  /** 画像生成（プロンプト入力モード） */
  IMAGE_GENERATION: 'image_generation_prompt_history',
  /** 画像生成（属性選択モード・その他のプロンプト） */
  IMAGE_GENERATION_ATTRIBUTES: 'image_generation_attributes_prompt_history',
  /** 動画生成（プロンプト入力モード） */
  VIDEO_GENERATION: 'video_generation_prompt_history',
  /** 動画シナリオ生成（追加プロンプト） */
  SCENARIO_ADDITIONAL_PROMPT: 'scenario_additional_prompt_history',
  /** 動画エディタ AI 編集（編集指示） */
  VIDEO_EDIT_INSTRUCTION: 'video_edit_instruction_prompt_history',
  /** スクロール動画の背景画像 AI 生成（表示するテキスト） */
  SWIPE_BACKGROUND_TEXT: 'swipe_background_text_prompt_history',
  /** スクロール動画の背景画像 AI 生成（補足） */
  SWIPE_BACKGROUND_EXTRA: 'swipe_background_extra_prompt_history',
  /** シーン単位のフレーム生成の追加プロンプト (調整ダイアログ) */
  SCENE_FRAME_ADDITIONAL: 'scene_frame_additional_prompt_history',
} as const;
